import type { TransitionFn } from "react-spring";
import { useTransition } from "react-spring";
import type { Lookup } from "@react-spring/types";

import type { NotificationItem } from "./types";

const paddingHeightPx = 16;
const marginPx = 8;
const paddingPx = 8;

const useTransitions = <D extends NotificationItem>(
  stack: D[],
  showStack: boolean,
  itemHeight: number,
  actionsHeight: number,
  notifications?: D[]
): {
  stackTransitions: TransitionFn<D, Lookup<unknown>>;
  notificationTransitions: TransitionFn<D, { right: number; top: number }>;
  actionTransitions: TransitionFn<boolean, { opacity: number }>;
} => {
  const isStackMultiple = stack.length > 1;

  const stackUpdateTransitions = (_: D, index: number) => {
    const isAfterFirstItem = isStackMultiple && index >= 1;
    let top = index * (itemHeight + paddingHeightPx + marginPx);
    top += actionsHeight + marginPx;
    let transform = showStack ? `translate(0px, ${top}px)` : "translate(0px, 0px)";

    if (isAfterFirstItem) {
      transform = showStack ? `translate(0px, ${top}px)` : `translate(-${paddingPx}px, ${itemHeight - paddingPx}px)`;
    }

    return {
      transform,
      paddingLeft: isAfterFirstItem && !showStack ? 0 : paddingPx,
      paddingRight: isAfterFirstItem && !showStack ? 0 : paddingPx,
    };
  };

   
  const stackTransitions = useTransition<D, object>(stack, {
    keys: (item: D) => item.key,
    from: (_, index: number) => {
      const isAfterFirstItem = isStackMultiple && index >= 1;
      let top = index * (itemHeight + paddingHeightPx + marginPx);
      top += actionsHeight + marginPx;

      let transform = showStack ? `translate(600px, ${top}px)` : "translate(600px, 0px)";

      if (isAfterFirstItem) {
        transform = showStack ? `translate(600px, ${top}px)` : `translate(600px, ${itemHeight - paddingPx}px)`;
      }

      return {
        transform,
      };
    },
    enter: stackUpdateTransitions,
    leave: (_, index: number) => {
      let top = index * (itemHeight + paddingHeightPx + marginPx);
      top += actionsHeight + marginPx;
      return { transform: showStack ? `translate(600px, ${top}px)` : "translate(600px, 0px)" };
    },
    update: stackUpdateTransitions,
  });

  const notificationTransitions = useTransition(notifications ?? [], {
    keys: (item) => item.key,
    from: { right: -600, top: 0 },
    enter: (_, index) => {
      const top = index * (itemHeight + paddingHeightPx + marginPx);
      return { right: 0, top };
    },
    update: (_, index) => {
      const top = index * (itemHeight + paddingHeightPx + marginPx);
      return { top };
    },
    leave: { right: -600 },
    delay: 0,
  });

  const actionTransitions = useTransition(showStack, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    reverse: showStack,
  });

  return { stackTransitions, notificationTransitions, actionTransitions };
};

export default useTransitions;
